import React from "react";

import { Type } from "common/entities/IMessage";
import Loading from "common/fragments/Loading";
import NonIdealState from "common/fragments/NonIdealState";
import { HTML } from "common/fragments/TextContent";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { BoxVariant, BoxView } from "common/views/box/Box.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { DropdownSelectVariant } from "common/views/dropdownSelect/DropdownSelect.view";
import { OptionDropdownSelect } from "common/views/dropdownSelect/OptionDropdownSelect";
import { Query } from "core/api/typedQuery";
import { IPanelProps, Panel } from "core/decorators/Panel";
import { isEmpty } from "core/util/ArrayUtils";
import { validateEnum, validateList } from "core/util/ParamUtil";
import StringUtil from "core/util/StringUtil";
import { iDef, iDefL } from "core/util/TypeUtils";
import labelProvider from './Announcements.labels';
import Config from "./Config";
import MessageUser from "./entities/MessageUser";

const {Labels} = labelProvider;


interface IMessageUserParams {
	sort?: string;
	type?: Type;
}

const MessagesQuery = Query.queryMany<MessageUser, IMessageUserParams>(MessageUser);

export const PARAM_MESSAGES_SORT = "messages.sort";
export const PARAM_MESSAGES_TYPE = "messages.type";
export const PARAM_MESSAGES_PAGE_SIZE = "messages.pageSize";

const sortOptions = [
	{value: "startDate", label: <Labels.StartDate/>},
	{value: "name", label: <Labels.Title/>}
];

const typeOptions = [
	{value: undefined, label: <Labels.All/>},
	{value: Type.ANNOUNCEMENT, label: <Labels.Announcement />},
	{value: Type.INFO, label: <Labels.Info />},
	{value: Type.REMINDER, label: <Labels.Reminder />},
	{value: Type.CRITICAL, label: <Labels.Critical/>},
	{value: Type.ALERT, label: <Labels.Alert/>}
];

@Panel({id: "announcements", configClass: Config, pageParams: true, labelProvider})
export class AnnouncementsPanel extends React.PureComponent<IPanelProps<Config>> {

	public render() {
		const { config, pageParams } = this.props;

		const defaultPageSize: number = iDef(config.pageSize) ? config.pageSize : 4;
		const currentPageSize: number = StringUtil.toInteger(pageParams[PARAM_MESSAGES_PAGE_SIZE], defaultPageSize);

		const params = config.enableRefine ? {
			sort: validateList(["startDate", "name"], pageParams[PARAM_MESSAGES_SORT], null),
			type: validateEnum<Type>(Type, pageParams[PARAM_MESSAGES_TYPE], null)
		} : {};

		return [
			config.enableRefine && (
				<BlockView key="refine" variant={BlockVariant.REFINE} style={{display: "flex"}} viewKey="refine">
					<OptionDropdownSelect
						key="refine-sort"
						options={sortOptions}
						onChange={this.onChangeParam(PARAM_MESSAGES_SORT)}
						value={pageParams[PARAM_MESSAGES_SORT]}
						variant={DropdownSelectVariant.MINIMAL}
						viewKey="refine.sort"
					>
						<Labels.Sort />
					</OptionDropdownSelect>
					<OptionDropdownSelect
						key="refine-type"
						options={typeOptions}
						onChange={this.onChangeParam(PARAM_MESSAGES_TYPE)}
						value={pageParams[PARAM_MESSAGES_TYPE]}
						variant={DropdownSelectVariant.MINIMAL}
						viewKey="refine.type"
					>
						<Labels.Type />
					</OptionDropdownSelect>
				</BlockView>
			),
			<MessagesQuery key="query" params={params}>
				{
					({data, loading}) => {
						if (loading) {
							return <Loading key="loading" />;
						}

						if (isEmpty(data)) {
							return (
								<NonIdealState
									key="empty"
									title={<Labels.MessageEmpty />}
									description={<Labels.DescriptionEmpty />}
								/>
							);
						}

						return (
							<div key="data">
								{data.map(this.renderMessage)}
								{
									config.enableLoadMore && data.length >= currentPageSize && (
										<BlockView variant={BlockVariant.PAGING} viewKey="paging">
											<ButtonView
												onClick={() => this.onChangeParam(PARAM_MESSAGES_PAGE_SIZE)(currentPageSize + defaultPageSize)}
												variant={ButtonVariant.PRIMARY}
												viewKey="load-more"
											>
												<Labels.LoadMore />
											</ButtonView>
										</BlockView>
									)
								}
							</div>
						);
					}
				}
			</MessagesQuery>
		];
	}

	private renderMessage = (message: MessageUser) => (
		<BoxView variant={BoxVariant.MINIMAL} title={<HTML content={message.summary} />} viewKey="message">
			<HTML content={message.description} />
		</BoxView>
	)

	private onChangeParam = (name: string) => (value: any) => {
		if (iDefL(value)) {
			this.props.actions.pushPageParam(name, value);
		} else {
			this.props.actions.removePageParam(name);
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/announcements/Announcements.panel.tsx